import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Assignment as TaskIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  AccountTree as WorkflowIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { processApi, taskApi, documentApi } from '../api/services';
import { useCryptoKeyStore, useEncryption, isEncryptedProcessVariable } from '../features/encryption';
import { BpmnViewer, ProcessControlPanel } from '../features/bpmn';

export function ProcessDetailPage() {
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeKey } = useCryptoKeyStore();
  const { decryptFields, isReady: encryptionReady } = useEncryption();
  const isAuthority = activeKey?.ownerType === 'AUTHORITY' || activeKey?.ownerType === 'ADMIN';

  const [decryptedVariables, setDecryptedVariables] = useState<Record<string, unknown> | null>(null);
  const [decryptionError, setDecryptionError] = useState<string | null>(null);

  const { data: process, isLoading: processLoading } = useQuery({
    queryKey: ['process', processId],
    queryFn: () => processApi.getProcess(processId!),
    enabled: !!processId,
  });

  const { data: variables } = useQuery({
    queryKey: ['process-variables', processId],
    queryFn: () => processApi.getProcessVariables(processId!),
    enabled: !!processId,
  });

  const { data: tasks } = useQuery({
    queryKey: ['process-tasks', processId],
    queryFn: () => taskApi.getTasksByProcess(processId!),
    enabled: !!processId,
  });

  const { data: documents } = useQuery({
    queryKey: ['process-documents', processId],
    queryFn: () => documentApi.getDocumentsByProcess(processId!),
    enabled: !!processId,
  });

  const cancelMutation = useMutation({
    mutationFn: () => processApi.cancelProcess(processId!, 'Cancelled by user'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes'] });
      navigate('/processes');
    },
  });

  // Decrypt variables when loaded
  useEffect(() => {
    if (!variables || !encryptionReady) return;

    const hasEncryptedData = Object.values(variables).some(
      (value) => isEncryptedProcessVariable(value)
    );

    if (hasEncryptedData) {
      decryptFields(variables)
        .then((decrypted) => {
          setDecryptedVariables(decrypted);
          setDecryptionError(null);
        })
        .catch((error) => {
          console.warn('Could not decrypt variables:', error);
          setDecryptionError('Einige Daten konnten nicht entschlüsselt werden.');
          setDecryptedVariables(variables);
        });
    } else {
      setDecryptedVariables(variables);
    }
  }, [variables, encryptionReady, decryptFields]);

  const displayVariables = decryptedVariables ?? variables ?? {};
  const hasEncryptedData = Object.values(displayVariables).some(
    (value) => isEncryptedProcessVariable(value)
  );

  const downloadDocument = async (docId: string, fileName: string) => {
    const blob = await documentApi.downloadDocument(docId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (processLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!process) {
    return <Alert severity="error">Prozess nicht gefunden</Alert>;
  }

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/processes')}
        sx={{ mb: 2 }}
      >
        Zurueck zur Liste
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {process.processDefinitionName || process.processDefinitionKey}
                </Typography>
                {process.ended ? (
                  <Chip label="Abgeschlossen" color="default" />
                ) : (
                  <Chip label="Aktiv" color="success" />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Referenz: {process.businessKey || process.id}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {!process.ended && isAuthority && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                >
                  Prozess abbrechen
                </Button>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkflowIcon />
                Workflow
              </Typography>
              <BpmnViewer
                processDefinitionKey={process.processDefinitionKey}
                processInstanceId={processId}
                showExecutionState={true}
                height={400}
              />
            </CardContent>
          </Card>

          {tasks && tasks.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TaskIcon />
                  Aktuelle Aufgaben
                </Typography>
                <List>
                  {tasks.map((task) => (
                    <ListItem
                      key={task.id}
                      disablePadding
                      sx={{ mb: 1 }}
                      secondaryAction={
                        <Chip
                          label={task.assignee || 'Offen'}
                          size="small"
                          color={task.assignee ? 'success' : 'warning'}
                        />
                      }
                    >
                      <ListItemButton
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
                      >
                        <ListItemText
                          primary={task.taskDisplay?.labels['de'] || task.name}
                          secondary={`Erstellt: ${task.createTime ? new Date(task.createTime).toLocaleString('de-DE') : '-'}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {documents && documents.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon />
                  Dokumente
                </Typography>
                <List>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.id}
                      sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}
                      secondaryAction={
                        <IconButton
                          onClick={() => downloadDocument(doc.id, doc.fileName)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={doc.fileName}
                        secondary={`${doc.category} - ${new Date(doc.uploadedAt).toLocaleString('de-DE')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {isAuthority && !process.ended && processId && (
            <ProcessControlPanel
              processInstanceId={processId}
              onVariablesChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['process-variables', processId] });
              }}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>

              <Box sx={{ '& > div': { mb: 2 } }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Prozess-ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {process.id}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Gestartet am
                  </Typography>
                  <Typography variant="body2">
                    {process.startTime
                      ? new Date(process.startTime).toLocaleString('de-DE')
                      : '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Gestartet von
                  </Typography>
                  <Typography variant="body2">
                    {process.startUserId || '-'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {Object.keys(displayVariables).length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Antragsdaten
                </Typography>

                {decryptionError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {decryptionError}
                  </Alert>
                )}

                {hasEncryptedData && (
                  <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
                    Einige Daten sind verschlüsselt.
                  </Alert>
                )}

                <Box sx={{ '& > div': { mb: 1 } }}>
                  {Object.entries(displayVariables)
                    .filter(([key, value]) =>
                      !['dataConsent', 'truthDeclaration', 'initiator'].includes(key) &&
                      !key.startsWith('_encrypted') &&
                      !isEncryptedProcessVariable(value)
                    )
                    .map(([key, value]) => (
                      <Box key={key}>
                        <Typography variant="caption" color="text.secondary">
                          {key}
                        </Typography>
                        <Typography variant="body2">
                          {String(value || '-')}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
